import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, RefreshCw, Loader2, Download } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Btn, Badge } from "@/components/kit";

const STATUS_TONE = { ready: "pos", partially_processed: "warn", failed: "neg", deleted: "neg" };
const STATUS_LABEL = { ready: "Pronto", partially_processed: "Parcial", failed: "Falhou", deleted: "Excluído" };
export const VIS = ["department", "company", "private", "manager_only", "project_team", "commercial_restricted", "finance_restricted", "hr_restricted", "legal_restricted", "administrative_restricted", "operations_restricted", "executive"];
export const VIS_LABEL = {
  department: "Departamento", company: "Empresa", private: "Privado", manager_only: "Somente gestão",
  project_team: "Equipe do projeto", commercial_restricted: "Comercial (restrito)", finance_restricted: "Financeiro (restrito)",
  hr_restricted: "RH (restrito)", legal_restricted: "Jurídico (restrito)", administrative_restricted: "Administrativo (restrito)",
  operations_restricted: "Operações (restrito)", executive: "Diretoria",
};

export default function Documents({ embedded = false }) {
  const { user } = useAuth();
  const isMgr = ["manager", "owner", "admin", "superadmin", "operador"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [visibility, setVisibility] = useState("department");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, []);

  const shown = filter === "all" ? docs : docs.filter((d) => (d.visibility_scope || "") === filter);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("visibility_scope", visibility);
    try {
      const { data } = await api.post("/documents/upload", fd);
      toast.success(data.duplicate ? "Documento já existia (duplicado)" : "Documento enviado — processando");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); load(); };
  const reprocess = async (id) => { try { await api.post(`/documents/${id}/reprocess`); toast.success("Reindexando"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const download = async (d) => {
    try {
      const { data } = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = d.name || "documento";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded
          ? <p className="text-sm text-zinc-500">Documentos que alimentam o cérebro — status do processamento.</p>
          : <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Documentos</h1>}
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="doc-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-[#0b1220]/40 focus:outline-none focus:ring-4 focus:ring-[#0b1220]/5"
            title="Filtrar documentos por visibilidade">
            <option value="all">Todos os documentos</option>
            {VIS.map((v) => <option key={v} value={v}>{VIS_LABEL[v] || v}</option>)}
          </select>
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
            <span className="hidden text-xs font-medium text-slate-500 sm:inline">Enviar como:</span>
            <select data-testid="doc-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)}
              className="bg-transparent text-sm text-slate-700 focus:outline-none"
              title="Visibilidade do próximo documento enviado">
              {VIS.map((v) => <option key={v} value={v}>{VIS_LABEL[v] || v}</option>)}
            </select>
          </div>
          <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="doc-upload-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Enviar PDF
          </Btn>
          <input ref={fileRef} type="file" className="hidden" onChange={upload} accept=".pdf" />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {shown.map((d) => {
          const pct = d.total_pages ? Math.round((d.pages_processed / d.total_pages) * 100) : 0;
          const processing = !["ready", "partially_processed", "failed", "deleted"].includes(d.status);
          return (
            <Card key={d.id} className="p-4" data-testid={`doc-${d.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <FileText size={18} className="mt-0.5 text-indigo-600" />
                  <div>
                    <p className="font-medium text-zinc-900">{d.name} <span className="text-xs text-zinc-400">v{d.version}</span></p>
                    <p className="text-xs text-zinc-500">{d.owner_name} · {VIS_LABEL[d.visibility_scope] || d.visibility_scope}</p>
                  </div>
                </div>
                <Badge tone={STATUS_TONE[d.status] || "primary"}>{processing ? "Processando" : (STATUS_LABEL[d.status] || d.status)}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                <div><p className="font-display text-sm font-semibold text-zinc-900">{d.total_pages}</p><p className="text-zinc-400">págs</p></div>
                <div><p className="font-display text-sm font-semibold text-zinc-900">{d.pages_ocr}</p><p className="text-zinc-400">OCR</p></div>
                <div><p className="font-display text-sm font-semibold text-zinc-900">{d.chunks}</p><p className="text-zinc-400">trechos</p></div>
                <div><p className="font-display text-sm font-semibold text-red-600">{d.pages_failed}</p><p className="text-zinc-400">falhas</p></div>
              </div>
              {processing && (
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div className="h-full bg-indigo-600 transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`} className="text-zinc-400 hover:text-indigo-600" title="Baixar para o computador"><Download size={15} /></button>
                {isMgr && (
                  <button onClick={() => reprocess(d.id)} data-testid={`doc-reprocess-${d.id}`} className="text-zinc-400 hover:text-indigo-600" title="Reindexar"><RefreshCw size={15} /></button>
                )}
                {(isMgr || d.owner_employee_id === user?.id) && (
                  <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`} className="text-zinc-400 hover:text-red-600" title="Excluir"><Trash2 size={15} /></button>
                )}
              </div>
            </Card>
          );
        })}
        {!shown.length && <p className="text-sm text-slate-500">{docs.length ? "Nenhum documento nesse filtro." : "Sem registros"}</p>}
      </div>
    </div>
  );
}
